import { useCallback, useContext } from 'react';
import { Menu, Submenu, useRootMenuContext } from '@shared/components/Menu';
import { useToast } from '@shared/components/toast';
import { useDeleteEntitiesMutation } from '@shared/scenes/cards/entityBulkActions/useDeleteWorkflowEntities';
import { i18n } from '@shared/util/i18n';
import { ChannelPermissionsContext } from '@shared/util/permissions/ChannelPermissionsContext';

interface Props {
  entityId: string;
  workflowTemplateId: string;
  onDelete?: VoidFunction;
}

export function DeleteSubmenu({ entityId, workflowTemplateId, onDelete }: Props) {
  const { align } = useRootMenuContext();
  const { canDeleteCard } = useContext(ChannelPermissionsContext);
  const deleteEntities = useDeleteEntitiesMutation(workflowTemplateId);
  const { show: showToast } = useToast();

  const remove = useCallback(async () => {
    try {
      await deleteEntities([entityId]);
      showToast({
        icon: 'check-filled',
        message: i18n('card.details.deleteCompletedNoName'),
      });
      onDelete?.();
    } catch (error) {
      console.error(error);
      showToast({
        style: 'warning',
        message: i18n('errors.somethingWentWrong'),
      });
    }
  }, [deleteEntities, entityId, onDelete, showToast]);

  if (!canDeleteCard.enabled) {
    return null;
  }

  return (
    <Submenu align={align}>
      <Submenu.Trigger>
        <Menu.Item label={i18n('common.delete')} danger />
      </Submenu.Trigger>
      <Submenu.Content testID="delete-card-sheet">
        <Menu.Section title={i18n('card.details.deleteConfirmation.title')} />
        <Menu.Item
          label={i18n('card.details.deleteConfirmation.confirm')}
          danger
          onClick={remove}
        />
        <Menu.Item label={i18n('common.cancel')} />
      </Submenu.Content>
    </Submenu>
  );
}
